import { useRef, useState, useEffect } from "react"
import { tableStore } from "@/routes/org/_components/table/table-store"
import { detailInfoStore } from "@/routes/org/_components/modal/detail-info-store"
import { langStore } from "@/stores/lang-store"
import SortIcon from "../../../../components/icons/sort-icon"
import { ChevronIcon } from "pk-icons"

const GridTable = ({ headers = [], rows = [] }) => {
  const { lang } = langStore()
  const sort = tableStore((s) => s.sort)
  const openModal = detailInfoStore((s) => s.openModal)

  const scrollRef = useRef(null)
  const containerRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState("100%")

  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(true)
  const [addressFull, setAddressFull] = useState(false)
  const firstLeft = "left-0"
  const secondLeft = "left-40"

  const checkScroll = () => {
    const el = scrollRef.current
    const container = containerRef.current
    if (!el) return

    const needScroll = el.scrollWidth > container.clientWidth

    const isLeftShow = el.scrollLeft > 10
    const isRightShow = el.scrollLeft + container.clientWidth < el.scrollWidth - 10

    setShowLeft(isLeftShow)
    setShowRight(isRightShow)

    setContainerWidth(containerRef.current.clientWidth)
    setAddressFull(!needScroll)
  }

  useEffect(() => {
    checkScroll()
  }, [rows])

  useEffect(() => {
    window.addEventListener("resize", checkScroll)
  }, [])

  const scrollLeft = () => {
    scrollRef.current.scrollBy({
      left: -600,
      behavior: "smooth"
    })
  }

  const scrollRight = () => {
    scrollRef.current.scrollBy({
      left: 600,
      behavior: "smooth"
    })
  }

  return (
    <div ref={containerRef} className="w-full mt-4 flex flex-col rounded-xl">
      <div className="relative w-full rounded-lg">
        {showLeft && (
          <button
            onClick={scrollLeft}
            className="
                absolute left-85 top-3
                z-100 bg-white/15 shadow-md rounded-full 
                text-white hover:text-black
                w-7 h-7 flex justify-center items-center
                hover:bg-white cursor-pointer
              "
          >
            <ChevronIcon direction="left" size={20} />
          </button>
        )}

        {/* RIGHT BUTTON */}
        {showRight && (
          <button
            onClick={scrollRight}
            className="
                absolute -right-4 top-3
                z-100 bg-white/90 shadow-md rounded-full
                w-7 h-7 flex justify-center items-center
                hover:bg-white cursor-pointer
              "
          >
            <ChevronIcon direction="right" size={20} />
          </button>
        )}
      </div>

      <div ref={scrollRef} onScroll={checkScroll} className="overflow-x-auto scroll-hidden">
        {/* Header */}
        <article
          className="
            flex min-w-max h-13
            text-white font-semibold
            text-center text-sm
            py-3 relative top-0
          "
        >
          {headers.map((h, idx) => {
            const direction = h.sortable && h.key === sort.key ? sort.order : "none"

            return (
              <p
                key={h.key}
                onClick={h.onClick || null}
                style={{
                  width: h.key === "address" && addressFull ? undefined : h.width || "100%"
                }}
                className={`
                  flex items-center rounded-md py-1 select-none
                  ${h.key === "address" && addressFull ? "flex-1" : ""}
                  ${
                    idx === 0
                      ? `sticky ${firstLeft} pl-3 z-30 bg-[#086375]`
                      : idx === 1
                        ? `sticky ${secondLeft} pr-3 z-30 bg-[#086375] justify-center`
                        : "justify-center ml-2 z-20"
                  }
                  ${h.sortable ? "hover:bg-[#17b79a] cursor-pointer" : ""}
                `}
              >
                {h.label[lang]}
                {h.label.unit ? `(${h.label.unit[lang]})` : ""}
                {h.sortable && <SortIcon direction={direction} />}
              </p>
            )
          })}
        </article>
        <div
          style={{ width: containerWidth }}
          className="absolute top-14 left-0 right-0 h-13 bg-[#086375] rounded-lg"
        />

        {/* Body */}
        {rows.length > 0 ? (
          rows.map((hit, idx) => (
            <article
              key={idx}
              className={`
                flex min-w-max text-center items-center
                py-3 rounded-lg text-[#333]
                ${idx % 2 === 0 ? "bg-[#fafafa]" : "bg-white"}
                shadow-sm
              `}
            >
              {headers.map((h, i) => {
                if (i === 0) {
                  return (
                    <button
                      key={h.key}
                      style={{ width: h.width || "100%" }}
                      className={`
                        sticky ${firstLeft} z-20 flex
                        text-left font-medium
                        bg-inherit
                        text-blue-700 hover:underline cursor-pointer
                      `}
                      onClick={() => openModal(hit)}
                    >
                      <div className="w-3" />
                      {hit[h.key]}
                    </button>
                  )
                } else if (i === 1) {
                  return (
                    <div
                      key={h.key}
                      style={{ width: h.width || "100%" }}
                      className={`
                        sticky ${secondLeft} pr-3 z-20
                        bg-inherit truncate`}
                      title={hit[h.key]}
                    >
                      {hit[h.key]}
                    </div>
                  )
                } else {
                  return (
                    <div
                      key={h.key}
                      style={{
                        width: h.key === "address" && addressFull ? undefined : h.width || "100%"
                      }}
                      title={h.key === "address" ? hit[h.key] : null}
                      className={`
                        ml-2 min-w-0
                        ${
                          h.key === "address"
                            ? addressFull
                              ? "flex-1"
                              : "truncate"
                            : "flex justify-center items-center"
                        }
                      `}
                    >
                      {h.key === "leak_severity" ? hit[h.key][lang] : (hit[h.key] ?? "-")}
                    </div>
                  )
                }
              })}
            </article>
          ))
        ) : (
          <div className="text-center py-6 text-gray-400">조건결과가 없습니다.</div>
        )}
      </div>
    </div>
  )
}

export default GridTable
